import type { LucideIcon } from 'lucide-react'
import { Bell } from 'lucide-react'
import type { ReactNode } from 'react'
import { Link, useLocation } from 'react-router-dom'
import {
  ADMIN_NAV_ITEMS,
  MAIN_NAV_ITEMS,
  type NavItemConfig,
} from '../../config/navigation'
import { useAuth } from '../../context/AuthContext'
import { useNotifications } from '../../context/NotificationContext'
import { Logo } from '../brand/Logo'
import { Button } from '../ui/button'
import { UserMenu } from './UserMenu'
import { cn } from '../../lib/utils'

type AppShellProps = {
  children: ReactNode
}

type ResolvedNavItem = {
  to: string
  label: string
  icon: LucideIcon
}

function resolveNavItem(item: NavItemConfig, dashboardPath: string): ResolvedNavItem {
  return {
    label: item.label,
    icon: item.icon,
    to: item.to === 'dashboard' ? dashboardPath : item.to,
  }
}

function filterNavItems(
  items: NavItemConfig[],
  can: (permission: string) => boolean,
  dashboardPath: string,
): ResolvedNavItem[] {
  return items
    .filter((item) => !item.permission || can(item.permission))
    .map((item) => resolveNavItem(item, dashboardPath))
}

function NavLink({ to, label, icon: Icon }: ResolvedNavItem) {
  const location = useLocation()
  const active = location.pathname === to
  const className = cn('nav-item', active && 'nav-item-active')

  return (
    <Link to={to} className={className}>
      <span className="nav-item-bar" />
      <Icon className="h-4 w-4" />
      <span className="flex-1">{label}</span>
    </Link>
  )
}

export function AppShell({ children }: AppShellProps) {
  const { roleLabel, dashboardPath, logout, can } = useAuth()
  const { unreadCount } = useNotifications()
  const mainNav = filterNavItems(MAIN_NAV_ITEMS, can, dashboardPath)
  const adminNav = filterNavItems(ADMIN_NAV_ITEMS, can, dashboardPath)
  const canViewNotifications = can('notifications-list')
  const notificationsActive = useLocation().pathname === '/notifications'

  return (
    <div className="min-h-screen">
      {/*
        Header stays above sidebar/main (z-[100]). Account controls are isolated on the
        right so nothing in the middle of the bar can intercept their clicks.
      */}
      <header className="fixed inset-x-0 top-0 z-[100] flex h-14 items-center justify-between gap-4 border-b border-parch-100 bg-white px-4">
        <div className="flex min-w-0 items-center gap-4">
          <div className="w-56 shrink-0 overflow-hidden">
            <Logo size="sm" />
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          <span className="badge badge-success hidden max-w-[10rem] truncate sm:inline-flex">
            {roleLabel}
          </span>
          {canViewNotifications && (
            <Link
              to="/notifications"
              aria-label="Notifications"
              className={cn(
                'relative grid h-8 w-8 place-items-center rounded-full text-ink-600 transition hover:bg-parch-50 hover:text-ink-900',
                notificationsActive && 'bg-parch-50 text-green-700',
              )}
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 inline-flex min-w-4 items-center justify-center rounded-full bg-green-600 px-1 py-0.5 text-[10px] font-semibold leading-none text-white">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </Link>
          )}
          <UserMenu />
          <Button variant="secondary" size="sm" onClick={() => void logout()}>
            Logout
          </Button>
        </div>
      </header>

      <aside className="fixed top-14 bottom-0 left-0 z-30 w-56 overflow-x-hidden overflow-y-auto border-r border-parch-100 bg-white py-3">
        <nav className="space-y-0.5 px-2 text-ink-600">
          {mainNav.length > 0 && (
            <>
              <p className="section-label px-3 pt-2 pb-1">Main</p>
              {mainNav.map((item) => (
                <NavLink key={`${item.to}-${item.label}`} {...item} />
              ))}
            </>
          )}
          {adminNav.length > 0 && (
            <>
              <p className="section-label px-3 pt-4 pb-1">Others</p>
              {adminNav.map((item) => (
                <NavLink key={`${item.to}-${item.label}`} {...item} />
              ))}
            </>
          )}
        </nav>
      </aside>

      <main className="relative z-0 pt-14 pl-56">
        <div className="mx-auto max-w-[1200px] p-6">{children}</div>
      </main>
    </div>
  )
}
